import { NavController } from 'ionic-angular';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';

import { EmptySubscriber } from '../../../../providers/api/api';
import { LoadingAlertProvider } from './../../../../providers/loading-alert/loading-alert';
import { MessageModel } from './../../../../models/message';
import { MessageProvider } from '../../../../providers/message/message';
import { NoteModel } from './../../../../models/note';
import { OpsOrderModel, OrderStatus } from './../../../../models/order.ops';
import { OpsProvider } from './../../../../providers/ops/ops';
import { OrderNotesPage } from './../../../order-notes/order-notes';
import { UserMessagesPage } from './../../../user-messages/user-messages';

@Component({
  selector: 'order-detail-info-card',
  templateUrl: 'order-detail-info-card.html',
})
export class OrderDetailInfoCardComponent implements OnInit, OnDestroy {
  @Input() order: OpsOrderModel;

  notes: NoteModel[] = [];
  messages$ = new BehaviorSubject<MessageModel[]>(null);

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private navCtrl: NavController,
    private opsService: OpsProvider,
    private loadingAlert: LoadingAlertProvider,
    private msgService: MessageProvider
  ) {}

  ngOnInit() {
    this.getNotes();
    this.startPolling();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.stopPolling();
  }

  // Messages

  private messagesSubscription: Subscription;

  startPolling() {
    let poll$ = Observable.interval(5000).startWith(0);
    let messages$ = poll$.switchMap(() => {
      this.opsService.getOrder(this.order.uid);
      return this.msgService.getMessagesForUser(this.order.user.uid);
    });
    this.messagesSubscription = messages$
      .takeUntil(this.unsubscribe)
      .subscribe(messages => {
        this.messages$.next(messages);
      });
  }

  stopPolling() {
    if (this.messagesSubscription) {
      this.messagesSubscription.unsubscribe();
    }
  }

  restartPolling() {
    this.stopPolling();
    this.startPolling();
  }

  // Notes

  getNotes() {
    this.opsService.getCustomerNotes(this.order.user.uid).subscribe(notes => {
      if (notes) {
        this.notes = notes;
      }
    });
  }

  // Data

  refreshMeetingURL() {
    this.loadingAlert.showLoaderWithText('Creating a meeting URL...');
    this.opsService
      .createMeetingURL(this.order.uid)
      .finally(() => {
        this.loadingAlert.dismissLoader();
      })
      .subscribe(
        res => {
          this.loadingAlert.showToastConfirmation('Meeting URL created');
          console.log(res);
        },
        error => {
          this.loadingAlert.showToastAlert(error);
          console.error(error);
        }
      );
  }

  // Actions
  didTapCreateMeeting() {
    this.loadingAlert.showConfirmationDialog(
      'Are you sure you want create a new meeting URL? This will be sent to the customer.',
      'Yes',
      () => {
        this.refreshMeetingURL();
      }
    );
  }

  didTapNotes() {
    this.navCtrl.push(OrderNotesPage, { order: this.order, notes: this.notes });
  }

  didTapMessages() {
    this.navCtrl.push(UserMessagesPage, {
      messages$: this.messages$,
      order: this.order,
      onMessageSend: () => this.messageSentHandler(),
    });
  }

  private messageSentHandler() {
    if (
      this.order.virtual &&
      this.order.status === OrderStatus.Pending
    ) {
      this.silentlyClaimOrder();
    }
    this.restartPolling();
  }

  private silentlyClaimOrder() {
    // silently send out an API request to claim the order
    this.opsService.startDrivingToOrder(this.order).subscribe(
      () => {
        new EmptySubscriber();
      },
      () => this.loadingAlert.showToastAlert('Error claiming order')
    );
  }

  didTapNote() {
    let latest = this.notes[0];
    if (latest.url) {
      window.open(latest.url, '_blank');
    }
  }

  // Helpers

  messagePreview() {
    let messages = this.messages$.getValue();
    if (messages && messages.length) {
      let latest = messages[messages.length - 1];
      return `${latest.yoshi ? 'Yoshi' : 'Customer'}: "${latest.body}"`;
    } else {
      return 'No messages';
    }
  }

  get noteLatestMessage() {
    let latest = this.notes[0];
    if (latest) {
      return latest.formattedMessage;
    } else {
      return 'No Notes';
    }
  }

  notePreviewLinkText() {
    let notes = this.notes;
    if (notes && notes.length && notes.length == 2) {
      return `(${notes.length - 1} More Note)`;
    } else if (notes && notes.length && notes.length > 2) {
      return `(${notes.length - 1} More Notes)`;
    } else {
      return '(Add Note)';
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-detail/components/order-detail-info-card/order-detail-info-card.ts